import { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query';
import { BaseQueryParams } from 'types/base-query-params';
import { PaginatedResponse } from 'types/dtos';
import { ResponsePlaceDto } from '../types/dtos';
import { constructQueryParams } from 'utils/query-params-builder';
import { APP_ENDPOINTS } from 'app/endpoints';
import { handleDuplicates, removeUndefined } from 'utils/array-helpers';
import { ResponseTerminalDto } from 'features/terminal/types/dtos';
import { ResponseWebsiteDto } from 'features/website/types/dtos';
import { Document } from 'services/documents/types/dtos';
import { checkError, fetchResponse } from 'utils/api-helpers';
import { formatDate } from 'utils/date-helpers';
import { ResponseCityDto } from 'features/city/types/dtos';
import { store } from 'store/index';

export type PlacesWithJoinQueryParams = BaseQueryParams & {
    terminalId?: string;
    website_id?: string;
};

export default (builder: EndpointBuilder<BaseQueryFn, string, string>) =>
    builder.query<PaginatedResponse<ResponsePlaceDto>, PlacesWithJoinQueryParams>({
        async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
            const websiteId = store.getState().workspace.websiteId;
            const params: PlacesWithJoinQueryParams = { ..._arg, website_id: websiteId };

            const placesResponse = await fetchWithBQ(constructQueryParams(APP_ENDPOINTS.PLACES, params));
            const placesError = checkError(placesResponse);
            if (placesError) return placesError;
            const places = placesResponse.data as PaginatedResponse<ResponsePlaceDto>;

            const terminalIds = removeUndefined(
                handleDuplicates(places.content.map((terminal) => terminal.terminalId)),
            );
            const terminalsResponse = await fetchResponse(APP_ENDPOINTS.TERMINALS, terminalIds, fetchWithBQ);
            const terminalError = checkError(terminalsResponse);
            if (terminalError) return terminalError;
            const terminals = terminalsResponse.data as PaginatedResponse<ResponseTerminalDto>;

            const websiteIds = removeUndefined(handleDuplicates(places.content.map((website) => website.websiteId)));
            const websitesResponse = await fetchResponse(APP_ENDPOINTS.WEBSITES, websiteIds, fetchWithBQ);
            const websiteError = checkError(websitesResponse);
            if (websiteError) return websiteError;
            const websites = websitesResponse.data as PaginatedResponse<ResponseWebsiteDto>;

            const citiesIds = removeUndefined(handleDuplicates(places.content.map((city) => city.cityId)));
            const citiesResponse = await fetchResponse(APP_ENDPOINTS.CITIES, citiesIds, fetchWithBQ);
            const citiesError = checkError(citiesResponse);
            if (citiesError) return citiesError;
            const cities = citiesResponse.data as PaginatedResponse<ResponseCityDto>;

            let documents: PaginatedResponse<Document>;
            const documentIds = handleDuplicates(
                removeUndefined(places.content.map((document) => document.documentId)),
            );
            if (documentIds.length > 0) {
                const documentResponse = await fetchResponse(APP_ENDPOINTS.DOCUMENTS, documentIds, fetchWithBQ);
                const documentError = checkError(documentResponse);
                if (documentError) return documentError;
                documents = documentResponse.data as PaginatedResponse<Document>;
            }

            places.content = places.content.map((place) => {
                const terminalName = terminals.content.find((terminal) => {
                    return terminal.id === place.terminalId;
                })?.name;
                const terminalStatus = terminals.content.find((terminal) => {
                    return terminal.id === place.terminalId;
                })?.status;
                const websiteName = websites.content.find((website) => {
                    return website.id === place.websiteId;
                })?.domain;
                const cityName = cities.content.find((city) => {
                    return city.id === place.cityId;
                })?.name;

                const filePath = documents?.content.find((document) => {
                    return document.id === place.documentId;
                })?.filePaths;

                const createdAt = formatDate(place.createdAt);

                const imageUrl = filePath && JSON.parse(filePath).small;

                return {
                    ...place,
                    terminalName,
                    terminalStatus,
                    websiteName,
                    cityName,
                    createdAt,
                    imageUrl,
                };
            });

            return {
                data: places,
            };
        },
        providesTags: [{ type: 'places-with-join', id: 'GET-JOIN' }],
    });
